import { PlusIcon } from "@heroicons/react/24/outline";
import {
  ActionButton,
  Avatar,
  OfficeButton,
  OfficeHeader,
  Paginator,
  TableComponent,
} from "components";
import config from "config";
import { FC, useEffect, useMemo } from "react";
import { useSearch, useNavigate } from "react-location";
import { LocationGenerics } from "router/location";
import { useUrlState, withRoles, wrapClick } from "utils";
import CreateReInsurerContainer from "./create";
import { Action } from "components/buttons/action-button";
import ViewReInsurerContainer from "./view";
import UpdateInsurerContainer from "./update";
import CreateInsurerManagerContainer from "./add-manager";
import { useReInsurers } from "hooks/data/use-re-insurers";
import { Reinsurers_rows } from "_graphql/queries/__generated__/Reinsurers";
import RemoveContainer from "./remove";
import { usePagination } from "hooks";

type Props = {};

const ReInsurersPage: FC<Props> = (props) => {
  const [modal, setModal] = useUrlState("modal");
  const searchParams = useSearch<LocationGenerics>();
  const navigate = useNavigate<LocationGenerics>();
  const { limit, offset, setPage } = usePagination();
  const filter = useMemo(
    () => ({
      page: searchParams.page || 1,
      pageSize: searchParams.pageSize || 12,
      search: (searchParams.search || "").toString(),
      searchFields: ["code", "batchCode"],
      sort: searchParams.sort || "",
    }),
    [searchParams]
  );
  const { reInsurers, refetch, loading, count } = useReInsurers(filter);
  const dispatchAction =
    (id: string, action: Exclude<Action, "expand" | "goto" | "clone">) =>
      () => {
        navigate({
          search: (old) => ({
            ...old,
            id,
            modal: action,
          }),
        });
      };
  const expandItem = (id: string) => {
    navigate({
      to: `./${id}`,
    });
  };

  useEffect(() => {
    navigate({
      search: (old) => ({
        ...old,
        ...(!old?.page ? { page: config.constants.page } : {}),
        ...(!old?.pageSize ? { pageSize: config.constants.pageSize } : {}),
        ...(!old?.search ? { search: "" } : {}),
        ...(!old?.sort ? { sort: "code" } : {}),
        ...(!old?.viewType ? { viewType: "grid" } : {}),
      }),
    });
  }, [navigate]);

  return (
    <>
      <main className="flex-1 flex flex-col overflow-hidden bg-background-500">
        <OfficeHeader
          renderActions={() => (
            <OfficeButton
              type="button"
              onClick={wrapClick(() => setModal("create"))}
              size="sm"
            >
              <PlusIcon className="-ml-0.5 mr-2 h-4 w-4" aria-hidden="true" />
              Add New Re-insurer
            </OfficeButton>
          )}
        />
        <div className="flex flex-1 overflow-y-auto">
          <div className="flex-1 mx-auto min-w-0  py-4 sm:py-6 overflow-hidden overflow-y-auto light flex">
            <TableComponent
              title={"Insurers"}
              hasSearch
              data={reInsurers}
              defaultView="grid"
              // renderGridLoader={() => (
              //   <div className="flex space-x-3 items-center h-36 bg-gray-200 animate-pulse rounded-md"></div>
              // )}
              loading={loading}
              refetch={refetch}
              columns={[
                {
                  accessor: "re_company_name",
                  name: "Company Name",
                },
                {
                  accessor: "re_company_email",
                  name: "Email",
                },
                {
                  accessor: "re_company_website",
                  name: "Website",
                },
                {
                  accessor: "reinsurer_address.country",
                  name: "Country",
                },
                {
                  accessor: "",
                  name: "Actions",
                  render: (data: Reinsurers_rows) => (
                    <>
                      <ActionButton
                        action="view"
                        onClick={dispatchAction(
                          data?.reinsurer_id || "",
                          "view"
                        )}
                      />
                      <ActionButton
                        action="expand"
                        onClick={() => expandItem(data?.reinsurer_id || "")}
                      />
                      {withRoles(["CEO", "Senior Broking Officer", 'Finance Officer', "Broking Officer", "Frontline Executive", "General Manager", "Managing Director", "System Administrator"])(<ActionButton
                        action="update"
                        onClick={dispatchAction(
                          data?.reinsurer_id || "",
                          "update"
                        )}
                      />)}
                      {withRoles([])(<ActionButton
                        action="assign"
                        tooltip="add associate"
                        onClick={dispatchAction(
                          data?.reinsurer_id || "",
                          "assign"
                        )}
                      />)}
                      {withRoles(["CEO", "Senior Broking Officer", 'Finance Officer', "Broking Officer", "Frontline Executive", "General Manager", "Managing Director", "System Administrator"])(<ActionButton
                        action="remove"
                        onClick={dispatchAction(
                          data?.reinsurer_id || "",
                          "remove"
                        )}
                      />)}
                    </>
                  ),
                },
              ]}
              renderGridItem={({ item: person }) => (
                <li
                  key={person?.reinsurer_id || ""}
                  className="col-span-1 flex flex-col divide-y divide-card-600 rounded-lg bg-card-500 text-center shadow"
                >
                  <div className="flex flex-1 flex-col p-8">
                    <div className="flex items-center justify-center">
                      <Avatar
                        size="xl"
                        alt={person.re_abbrv?.split("").join(" ") || "N A"}
                      />
                    </div>
                    <h3 className="mt-6 text-sm font-medium text-primary">
                      {person.re_company_name}
                    </h3>
                    <dl className="mt-1 flex flex-grow flex-col justify-between">
                      <dt className="sr-only">Title</dt>
                      <dd className="text-sm text-tertiary">
                        {person.re_company_email}
                      </dd>
                    </dl>
                    <div className="mt-1 flex flex-col justify-between bg-primary-100 mx-auto px-3 rounded-3xl">
                      <span className="text-sm text-primary-500">
                        {person.re_company_website}
                      </span>
                    </div>
                  </div>
                  <div>
                    <div className="-mt-px flex divide-x divide-card-600">
                      <div className="flex w-0 flex-1">
                        <button className="relative -mr-px inline-flex w-0 flex-1 items-center justify-center gap-x-3 rounded-bl-lg border border-transparent py-4 text-sm font-semibold text-gray-900">
                          <ActionButton
                            action="view"
                            tooltip="View Reinsurer"
                            onClick={dispatchAction(person?.reinsurer_id || "", "view")}
                          />
                        </button>
                      </div>
                      <div className="-ml-px flex w-0 flex-1">
                        <button className="relative inline-flex w-0 flex-1 items-center justify-center gap-x-3 rounded-br-lg border border-transparent py-4 text-sm font-semibold text-gray-900">
                          <ActionButton
                            action="expand"
                            tooltip="Goto Reinsurer"
                            onClick={() => expandItem(person?.reinsurer_id || "")}
                          />
                        </button>
                      </div>
                      {withRoles(["CEO", "Senior Broking Officer", 'Finance Officer', "Broking Officer", "Frontline Executive", "General Manager", "Managing Director", "System Administrator"])(
                        <div className="-ml-px flex w-0 flex-1">
                          <button className="relative inline-flex w-0 flex-1 items-center justify-center gap-x-3 rounded-br-lg border border-transparent py-4 text-sm font-semibold text-gray-900">
                            <ActionButton
                              action="update"
                              tooltip="Update Reinsurer"
                              onClick={dispatchAction(person?.reinsurer_id || "", "update")}
                            />
                          </button>
                        </div>
                      )}
                      <div className="-ml-px flex w-0 flex-1">
                        <button className="relative inline-flex w-0 flex-1 items-center justify-center gap-x-3 rounded-br-lg border border-transparent py-4 text-sm font-semibold text-gray-900">
                          <ActionButton
                            action="assign"
                            tooltip="Add Associate"
                            onClick={dispatchAction(person?.reinsurer_id || "", "assign")}
                          />
                        </button>
                      </div>
                      {withRoles(["CEO", "Senior Broking Officer", 'Finance Officer', "Broking Officer", "Frontline Executive", "General Manager", "Managing Director", "System Administrator"])(
                        <div className="-ml-px flex w-0 flex-1">
                          <button className="relative inline-flex w-0 flex-1 items-center justify-center gap-x-3 rounded-br-lg border border-transparent py-4 text-sm font-semibold text-gray-900">
                            <ActionButton
                              action="remove"
                              onClick={dispatchAction(person?.reinsurer_id || "", "remove")}
                            />
                          </button>
                        </div>
                      )}
                    </div>
                  </div>
                </li>
              )}
              pagination={() => <Paginator offset={offset} limit={limit} totalItems={count} currentSize={reInsurers.length} setOffset={setPage} />}
            />
          </div>
        </div>
      </main>

      <CreateReInsurerContainer
        open={modal === "create"}
        setOpen={(val: boolean) => setModal(val ? "create" : undefined)}
        refetch={refetch}
      />

      {searchParams.id && (
        <>
          <ViewReInsurerContainer
            open={modal === "view"}
            setOpen={(val: boolean) => setModal(val ? "view" : undefined)}
          />
          <UpdateInsurerContainer
            open={modal === "update"}
            setOpen={(val: boolean) => setModal(val ? "update" : undefined)}
            refetch={refetch}
          />
          <CreateInsurerManagerContainer
            open={modal === "assign"}
            setOpen={(val: boolean) => setModal(val ? "assign" : undefined)}
            refetch={refetch}
          />
          <RemoveContainer
            open={modal === "remove"}
            setOpen={(val: boolean) => setModal(val ? "remove" : undefined)}
            refetch={refetch}
          />
        </>
      )}
    </>
  );
};

export default ReInsurersPage;
